import { useState } from "react";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export const PRIVACY_VERSIONE = "1.1";

// Iter122db · GDPR · Il titolare del trattamento è il tenant a cui è rilasciata la licenza,
// non "Amministrazioni Izzo". I dati vengono forniti dal backend via /auth/me → user.titolare_trattamento
// oppure dal prop `titolare` (per pagine pubbliche non autenticate).
function TitolareLine({ tit }) {
  if (!tit || !tit.ragione_sociale) {
    return (
      <p data-testid="privacy-titolare-generic">
        <b>Titolare del trattamento</b>: lo Studio di amministrazione che ha rilasciato le Sue credenziali di
        accesso a Ginius. I riferimenti completi (ragione sociale, sede, email, PEC) sono disponibili nella
        sezione &laquo;Impostazioni · Titolare del trattamento&raquo; una volta effettuato l&apos;accesso, oppure sui documenti/comunicazioni
        già ricevuti dallo Studio stesso.
      </p>
    );
  }
  const parts = [tit.ragione_sociale];
  if (tit.indirizzo_completo) parts.push(tit.indirizzo_completo);
  const contatti = [];
  if (tit.email) contatti.push(`email: ${tit.email}`);
  if (tit.pec) contatti.push(`PEC: ${tit.pec}`);
  if (tit.telefono) contatti.push(`tel: ${tit.telefono}`);
  const idFiscali = [];
  if (tit.partita_iva) idFiscali.push(`P.IVA ${tit.partita_iva}`);
  if (tit.codice_fiscale && tit.codice_fiscale !== tit.partita_iva) idFiscali.push(`C.F. ${tit.codice_fiscale}`);
  return (
    <p data-testid="privacy-titolare">
      <b>Titolare del trattamento</b>: {parts.join(" — ")}
      {contatti.length > 0 && <> — {contatti.join(" · ")}</>}
      {idFiscali.length > 0 && <> ({idFiscali.join(" · ")})</>}
      .
    </p>
  );
}

export function PrivacyInformativa({ titolare }) {
  const auth = useAuth();
  const tit = titolare || auth?.user?.titolare_trattamento || null;
  return (
    <div className="space-y-3 text-base text-slate-700 leading-relaxed">
      <TitolareLine tit={tit} />
      <p><b>Finalità del trattamento</b>: i dati personali e di contatto forniti (nome, recapiti, unità immobiliare,
        descrizione del guasto ed eventuali fotografie) sono trattati al solo scopo di gestire la richiesta di
        manutenzione, affidarla al fornitore competente e tenervi aggiornati sullo stato dell&apos;intervento.</p>
      <p><b>Comunicazione dei dati</b>: i dati strettamente necessari potranno essere comunicati ai fornitori
        incaricati dell&apos;intervento e, ove pertinente, alla compagnia assicurativa per la gestione di eventuali sinistri.</p>
      <p><b>Base giuridica e conservazione</b>: il trattamento si fonda sul consenso e sull&apos;esecuzione del servizio di
        amministrazione condominiale. I dati sono conservati per il tempo necessario alla gestione della pratica e agli
        adempimenti di legge.</p>
      <p><b>Diritti dell&apos;interessato</b>: ai sensi del Regolamento UE 2016/679 (GDPR) potete in ogni momento richiedere
        accesso, rettifica, cancellazione, limitazione o portabilità dei vostri dati, nonché opporvi al trattamento,
        scrivendo al Titolare ai recapiti sopra indicati.</p>
      <p className="text-xs text-slate-500 pt-2 border-t">
        Ginius è la piattaforma tecnologica utilizzata dal Titolare come Responsabile del trattamento ex art. 28 GDPR;
        Ginius non accede ai dati per finalità proprie e li conserva per conto del Titolare.
      </p>
    </div>
  );
}

export function PrivacyConsent({ checked, onChange, testid = "privacy-consenso", titolare }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="p-4 border-2 border-[#EBC5D2] bg-[#FBEEF3]/50 rounded-lg">
      <div className="flex items-start gap-3">
        <Checkbox data-testid={testid} checked={checked} onCheckedChange={onChange} className="mt-1 w-6 h-6" />
        <div className="text-base text-slate-800">
          <span className="font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-brand" /> Consenso al trattamento dei dati (obbligatorio)</span>
          <p className="mt-1">
            Dichiaro di aver letto l&apos;
            <button type="button" data-testid="open-privacy" onClick={() => setOpen(true)}
              className="text-brand font-semibold underline">informativa sulla privacy</button>
            {" "}e acconsento al trattamento dei miei dati per la gestione della richiesta di manutenzione.
          </p>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-brand" /> Informativa sul trattamento dei dati personali
            </DialogTitle>
            <DialogDescription className="text-base text-slate-600">Regolamento UE 2016/679 (GDPR)</DialogDescription>
          </DialogHeader>
          <PrivacyInformativa titolare={titolare} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
